import { ReactNode, useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, type AppRole } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { type Resource, type Action } from '@/lib/permissions';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldX, RefreshCw, UserPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { AUTH_STORAGE_KEY } from '@/integrations/supabase/rememberMeStorage';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Optional: Require one of these roles */
  requiredRoles?: AppRole[];
  /** Optional: Require permission for a resource/action */
  requiredPermission?: {
    resource: Resource;
    action: Action;
  };
  /** Skip the multi-org firm selection redirect (used by /select-firm route itself) */
  skipFirmCheck?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRoles,
  requiredPermission,
  skipFirmCheck,
}: ProtectedRouteProps) {
  const {
    isAuthenticated,
    initializing,
    hasPendingInvite,
    signOut,
    activeBusiness,
    isSuperAdmin,
    isOrgAdmin,
    contextError,
    contextReady,
    organizations,
    businessMemberships,
    needsFirmSelection,
  } = useAuth();
  const location = useLocation();
  
  // Get permissions for the required resource (if any)
  const permissions = usePermissions(requiredPermission?.resource ?? 'courts');

  // State for clearing stale metadata - MUST be before any early returns
  const [isClearing, setIsClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  // Handle "Remember me" session clearing on browser close
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleBeforeUnload = () => {
      // Check if user opted out of "Remember me"
      if (typeof window !== 'undefined' && sessionStorage.getItem('clearSessionOnClose') === 'true') {
        // Clear the auth session from localStorage
        // Note: This runs synchronously before page unload
        const storageKey = AUTH_STORAGE_KEY;
        localStorage.removeItem(storageKey);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAuthenticated]);

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 w-full max-w-md p-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-8 w-1/2" />
        </div>
      </div>
    );
  }

  const handleClearMetadata = async () => {
    setIsClearing(true);
    setClearError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setClearError('No active session found. Please sign out and sign in again.');
        return;
      }
      const { error } = await supabase.functions.invoke('clear-stale-metadata', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: {},
      });
      if (error) {
        setClearError('Failed to clear metadata. Please sign out and try again.');
      } else {
        // Metadata cleared, now sign out
        await signOut();
      }
    } catch {
      setClearError('Unexpected error. Please sign out and try again.');
    } finally {
      setIsClearing(false);
    }
  };

  // Show error if auth context failed to load
  if (contextError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-destructive/10">
                <ShieldX className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <CardTitle className="text-xl">Authentication Error</CardTitle>
            <CardDescription className="text-base">
              {contextError}
            </CardDescription>
            {clearError && (
              <p className="text-sm text-destructive mt-2">{clearError}</p>
            )}
          </CardHeader>
          <CardFooter className="flex flex-col gap-2">
            <Button 
              onClick={handleClearMetadata} 
              variant="default"
              disabled={isClearing}
              className="w-full"
            >
              {isClearing ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Clearing...
                </>
              ) : (
                'Clear Stale Data & Sign Out'
              )}
            </Button>
            <Button onClick={signOut} variant="outline" className="w-full">
              Sign Out
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (hasPendingInvite) {
    // Check for stored invite token and auto-redirect (SSR-safe)
    const storedToken = typeof window !== 'undefined' 
      ? sessionStorage.getItem('pendingInviteToken') 
      : null;
    
    if (storedToken) {
      return <Navigate to={`/invite/accept?token=${storedToken}`} replace />;
    }

    // No stored token - show user-friendly pending screen with guidance
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <UserPlus className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl">Complete Your Setup</CardTitle>
            <CardDescription className="text-base">
              Your account is created but not linked to a firm yet.
              Please check your email for an invitation link and click it again,
              or contact your administrator for a new invitation.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={signOut} variant="outline" className="w-full">
              Sign Out
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Multi-org users without an active business must pick a firm first
  if (!skipFirmCheck && needsFirmSelection) {
    return <Navigate to="/select-firm" replace />;
  }

  const hasOrgContext = organizations.length > 0 || businessMemberships.length > 0;
  if (contextReady && !isSuperAdmin && !hasOrgContext) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-destructive/10">
                <ShieldX className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <CardTitle className="text-xl">Organization Required</CardTitle>
            <CardDescription className="text-base">
              Your account is not linked to an organization yet. Please accept an invite
              or contact your administrator to complete setup.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex justify-center">
            <Button onClick={signOut} variant="outline" className="w-full">
              Sign Out
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Role-based access check
  if (requiredRoles && requiredRoles.length > 0) {
    const userRole = activeBusiness?.role;
    const hasRequiredRole = isSuperAdmin || isOrgAdmin || 
      (userRole && requiredRoles.includes(userRole));
    
    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Card className="max-w-md mx-4">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-destructive/10">
                  <ShieldX className="h-8 w-8 text-destructive" />
                </div>
              </div>
              <CardTitle className="text-xl">Access Denied</CardTitle>
              <CardDescription className="text-base">
                You don't have permission to access this page.
                {requiredRoles.length === 1 
                  ? ` This page requires the ${requiredRoles[0]} role.`
                  : ` This page requires one of: ${requiredRoles.join(', ')}.`
                }
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-center gap-2">
              <Button onClick={() => window.history.back()} variant="outline">
                Go Back
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }
  }

  // Permission-based access check
  if (requiredPermission) {
    const { action } = requiredPermission;
    const hasPermission = permissions.can(action);
    
    if (!hasPermission) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Card className="max-w-md mx-4">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-destructive/10">
                  <ShieldX className="h-8 w-8 text-destructive" />
                </div>
              </div>
              <CardTitle className="text-xl">Access Denied</CardTitle>
              <CardDescription className="text-base">
                {permissions.getDeniedReason(action) || 
                  "You don't have permission to access this page."}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-center gap-2">
              <Button onClick={() => window.history.back()} variant="outline">
                Go Back
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }
  }

  return <>{children}</>;
}
